import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const apiUrl = process.env.REACT_APP_API_URL;

export const usePermissionsSync = (intervalMs = 30000) => {
  const intervalRef = useRef(null);
  const lastSyncRef = useRef(Date.now());

  // ✅ حفظ الصلاحيات مباشرة في localStorage
  const savePermissionsToStorage = useCallback((permissions, role, user) => {
    try {
      console.log('💾 Saving permissions to localStorage:', permissions);
      console.log('💾 Saving role:', role);
      console.log('💾 Saving user:', user);
      
      // ✅ حفظ الصلاحيات
      localStorage.setItem('userPermissions', JSON.stringify(permissions));
      localStorage.setItem('userRole', role);
      
      // ✅ حفظ بيانات المستخدم
      if (user) {
        localStorage.setItem('userId', user.id);
        localStorage.setItem('username', user.username);
        localStorage.setItem('userEmail', user.email);
      }
      
      console.log('✅ Permissions saved successfully');
      console.log('📊 Total permissions:', permissions.length);
      console.log('📋 Permissions list:', permissions);
      
      // ✅ إطلاق Custom Event للـ Context
      window.dispatchEvent(new CustomEvent('permissionsUpdated', {
        detail: { permissions, role, user }
      }));
      
    } catch (error) {
      console.error('❌ Error saving permissions:', error);
    }
  }, []);

  const clearPermissionsFromStorage = useCallback(() => {
    console.log('🗑️ Clearing permissions from localStorage');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    
    // ✅ إطلاق Custom Event
    window.dispatchEvent(new Event('permissionsCleared'));
  }, []);

  const fetchLatestPermissions = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        console.log('⚠️ No token - skipping permissions sync');
        return;
      }

      console.log('🔄 Fetching latest permissions from API...');

      const response = await axios.get(`${apiUrl}/auth/me/permissions/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      console.log('📡 API Response:', response.data);

      if (response.data) {
        const { user, permissions } = response.data;
        
        // ✅ مقارنة الصلاحيات القديمة بالجديدة
        const oldPermissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
        
        console.log('📊 Old permissions count:', oldPermissions.length);
        console.log('📊 New permissions count:', permissions.length);
        
        // ✅ إيجاد الصلاحيات المحذوفة
        const removedPermissions = oldPermissions.filter(p => !permissions.includes(p));
        // ✅ إيجاد الصلاحيات الجديدة
        const addedPermissions = permissions.filter(p => !oldPermissions.includes(p));
        
        if (removedPermissions.length > 0) {
          console.log('🗑️ Removed permissions:', removedPermissions);
        }
        if (addedPermissions.length > 0) {
          console.log('➕ Added permissions:', addedPermissions);
        }
        
        // ✅ تحديث الصلاحيات بالقيم الجديدة من API
        savePermissionsToStorage(permissions, user.role, user);
        lastSyncRef.current = Date.now();
        
        console.log('✅ Permissions synced successfully at:', new Date().toLocaleTimeString());
        
      } else {
        console.warn('⚠️ API returned empty data:', response.data);
      }
    } catch (error) {
      console.error('❌ Error syncing permissions:', error);
      
      // لو 401 Unauthorized - امسح الصلاحيات واعمل logout
      if (error.response?.status === 401) {
        console.log('🔐 Unauthorized - clearing permissions and redirecting to login');
        clearPermissionsFromStorage();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
  }, [savePermissionsToStorage, clearPermissionsFromStorage]);

  const startSync = useCallback(() => {
    // مسح أي interval قديم
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    console.log(`🚀 Starting permissions sync (every ${intervalMs / 1000}s)`);

    // ✅ أول sync فوري
    fetchLatestPermissions();

    // ✅ بعدين sync دوري
    intervalRef.current = setInterval(() => {
      console.log('⏰ Running scheduled permissions sync...');
      fetchLatestPermissions();
    }, intervalMs);
    
    console.log(`✅ Permissions sync interval started`);
  }, [fetchLatestPermissions, intervalMs]);

  const stopSync = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log('⏹️ Permissions sync stopped');
    }
  }, []);

  const forceSync = useCallback(() => {
    console.log('🔄 Force syncing permissions NOW...');
    fetchLatestPermissions();
  }, [fetchLatestPermissions]);

  useEffect(() => {
    startSync();

    // Cleanup عند unmount
    return () => {
      console.log('🧹 Cleaning up permissions sync...');
      stopSync();
    };
  }, [startSync, stopSync]);

  return {
    forceSync,
    lastSync: lastSyncRef.current,
    startSync,
    stopSync
  };
};